import {
  Button,
  useToast,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
} from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import axios from 'axios';
import React, { useState, useEffect } from 'react';

import { url as commonUrl } from '@finley-technologies/common';

import {
  useGetLoanProductsAndAssociatedFacilitiesForCreditFacilityQuery,
  AssignmentConfirmationJobGQLType,
} from '../../../../../../types/dashboard-core-gqlTypes.generated.d';
import { RepurchaseDrawer } from './RepurchaseDrawer';
import {
  ActionsDrawerState,
  LoanProductCreditFacilityReceivableUploadMap,
  initialUploadState,
  ReceivableUpload,
} from './shared';
import { TransferDrawer } from './TransferDrawer';
import { Icon as FaIcon } from 'packages/common-frontend/components/icons';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';

export const ActionsDrawer = ({
  creditFacilityId,
  setInitiatedJobInfo,
  jobInProgress,
}: {
  creditFacilityId: string;
  setInitiatedJobInfo: React.Dispatch<
    React.SetStateAction<{
      id: string;
      jobType: Omit<AssignmentConfirmationJobGQLType['jobType'], 'assignment'>;
      receivableCount: number | null;
    } | null>
  >;
  jobInProgress: boolean;
}) => {
  const toast = useToast();
  const { accessToken } = useUserAuthOrThrow();

  const [
    actionsDrawerState,
    setActionsDrawerState,
  ] = useState<ActionsDrawerState | null>(null);

  const [
    receivableUploadsMap,
    setReceivableUploadsMap,
  ] = useState<LoanProductCreditFacilityReceivableUploadMap>({});

  const [kickingOffJob, setKickingOffJob] = useState<boolean>(false);

  const {
    data: creditFacilityData,
  } = useGetLoanProductsAndAssociatedFacilitiesForCreditFacilityQuery({
    variables: { creditFacilityId },
    onCompleted(data) {
      const receivableUploadMap: LoanProductCreditFacilityReceivableUploadMap = {};

      const { loanProducts } = data.creditFacility;

      loanProducts.forEach((loanProduct) => {
        receivableUploadMap[loanProduct.id] = {
          loanProductId: loanProduct.id,
          loanProductName: loanProduct.name,
          key: loanProduct.id,
          ...initialUploadState,
        };
      });

      setReceivableUploadsMap(receivableUploadMap);
    },
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    setReceivableUploadsMap((uploadsMap) => {
      const newUploadMap: LoanProductCreditFacilityReceivableUploadMap = {};

      Object.values(uploadsMap).forEach((upload) => {
        newUploadMap[upload.key] = {
          ...upload,
          ...initialUploadState,
        };
      });

      return newUploadMap;
    });
  }, [actionsDrawerState]);

  if (!creditFacilityData) {
    return null;
  }

  // show option to transfer if a loan product for the current credit facility
  // is associated with another credit facility i.e. there is the ability to transfer receivables
  const showTransferOption = creditFacilityData.creditFacility.loanProducts.some(
    (lp) =>
      lp.currentCreditFacilities?.some((cf) => cf.id !== creditFacilityId),
  );

  // Perform basic file structure validation on each file addition, checking that the file is single column.
  const validateFileStructure = async (
    file: File,
    receivableUpload: ReceivableUpload,
  ): Promise<void> => {
    // Reset error state and display loading icon
    setReceivableUploadsMap({
      ...receivableUploadsMap,
      [receivableUpload.key]: {
        ...receivableUploadsMap[receivableUpload.key],
        ...initialUploadState,
        isLoading: true,
      },
    });

    const url = commonUrl.getBorrowerExpressUrl(
      commonUrl.borrowerExpressPaths.assignmentCSVValidation,
    );
    const formData = new FormData();
    formData.append('loanProductId', receivableUpload.loanProductId);
    formData.append('creditFacilityId', creditFacilityId);
    formData.append('receivables', file);
    formData.append('assignmentType', `${actionsDrawerState}ByCSV`);

    try {
      const { data } = await axios.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          authorization: `Bearer ${accessToken.raw}`,
        },
      });

      const fileError = data.isValid ? '' : data.fileErrors[0];

      const updatedReceivableUpload = {
        ...receivableUploadsMap[receivableUpload.key],
        isValid: data.isValid,
        isLoading: false,
        fileError,
        file,
      };
      setReceivableUploadsMap({
        ...receivableUploadsMap,
        [updatedReceivableUpload.key]: updatedReceivableUpload,
      });
    } catch (err: any) {
      Sentry.captureException(JSON.stringify(err, null, 2));
      toast({
        ...errorToastConfig,
        id: undefined, // Avoid merging timeouts if multiple errors.
        title:
          'Error processing file. Please try again, and contact your Finley representative if this persists.',
      });
      const updatedReceivableUpload = {
        ...receivableUploadsMap[receivableUpload.key],
        // Stop the spinner.
        isLoading: false,
      };
      setReceivableUploadsMap({
        ...receivableUploadsMap,
        [updatedReceivableUpload.key]: updatedReceivableUpload,
      });
    }
  };

  const initiateReassignmentConfirmationJob = async (
    jobType: ActionsDrawerState,
  ): Promise<void> => {
    setKickingOffJob(true);
    const formData = new FormData();

    const uploadInfo: {
      loanProductId: string;
      creditFacilityId: string | null;
    }[] = [];

    Object.values(receivableUploadsMap).forEach((upload) => {
      if (upload.isValid && upload.file) {
        uploadInfo.push({
          loanProductId: upload.loanProductId,
          creditFacilityId: upload.creditFacilityId ?? null,
        });
        formData.append(`${upload.loanProductId}-file`, upload.file);
      }
    });

    const url = commonUrl.getBorrowerExpressUrl(
      commonUrl.borrowerExpressPaths.reassignByCSV,
    );
    formData.append(
      'implementationId',
      creditFacilityData.creditFacility.implementationId,
    );
    formData.append('uploadInfo', JSON.stringify(uploadInfo));
    formData.append('jobType', jobType);

    try {
      const { data } = await axios.post(url, formData, {
        timeout: 300000, // 5 minute timeout
        headers: {
          'Content-Type': 'multipart/form-data',
          authorization: `Bearer ${accessToken.raw}`,
        },
      });
      setInitiatedJobInfo({
        id: data.jobId,
        jobType,
        receivableCount: data.receivableCount,
      });
    } catch (err: any) {
      Sentry.captureException(JSON.stringify(err, null, 2));
      toast({
        ...errorToastConfig,
        id: undefined, // Avoid merging timeouts if multiple errors.
        title: `Error initiating receivable ${jobType}. Please try again, and contact your Finley representative if this persists.`,
      });
    } finally {
      setKickingOffJob(false);
    }
  };

  return (
    <>
      <Menu>
        <MenuButton
          as={Button}
          rightIcon={<FaIcon icon="chevron-down" />}
          variant="secondary-muted"
          isDisabled={jobInProgress}
          color="gray.800"
        >
          Actions
        </MenuButton>
        <MenuList zIndex={10}>
          <MenuItem onClick={() => setActionsDrawerState('repurchase')}>
            Repurchase by CSV
          </MenuItem>
          {showTransferOption && (
            <MenuItem onClick={() => setActionsDrawerState('transfer')}>
              Transfer by CSV
            </MenuItem>
          )}
        </MenuList>
      </Menu>
      {actionsDrawerState === 'repurchase' && (
        <RepurchaseDrawer
          onClose={() => setActionsDrawerState(null)}
          receivableUploadsMap={receivableUploadsMap}
          setReceivableUploadsMap={setReceivableUploadsMap}
          validateFileStructure={validateFileStructure}
          kickingOffJob={kickingOffJob}
          initiateReassignmentConfirmationJob={
            initiateReassignmentConfirmationJob
          }
        />
      )}
      {actionsDrawerState === 'transfer' && (
        <TransferDrawer
          onClose={() => setActionsDrawerState(null)}
          receivableUploadsMap={receivableUploadsMap}
          setReceivableUploadsMap={setReceivableUploadsMap}
          creditFacilityData={creditFacilityData}
          currentCreditFacilityId={creditFacilityId}
          validateFileStructure={validateFileStructure}
          kickingOffJob={kickingOffJob}
          initiateReassignmentConfirmationJob={
            initiateReassignmentConfirmationJob
          }
        />
      )}
    </>
  );
};
